import type { ReactNode, RefObject } from "react";

import { createContext } from "react";

import type {
  ClassNamesTypeProp,
  ColorKeys,
  Configs,
  DateLookingType,
  DatepickerTriggerProps,
  DateRangeType,
  DateType,
  DateValueType,
  Period,
  PopoverDirectionType,
  WeekStringType,
} from "../types";

import {
  DATE_FORMAT,
  DEFAULT_COLOR,
  DEFAULT_DATE_LOOKING,
  DEFAULT_SEPARATOR,
  LANGUAGE,
  START_WEEK,
} from "../constants";

type DatepickerStore = {
  arrowContainer: RefObject<HTMLDivElement | null> | null;
  asSingle?: boolean;

  calendarContainer: RefObject<HTMLDivElement | null> | null;
  cancelDatepicker: () => void;
  changeDatepickerValue: (
    value: DateValueType,
    e?: HTMLInputElement | null,
  ) => void;
  changeDayHover: (day: DateType) => void;
  changeInputText: (text: string) => void;
  changePeriod: (period: Period) => void;
  classNames?: ClassNamesTypeProp;
  configs?: Configs;
  containerClassName?: ((className: string) => string) | string | null;

  dateLooking?: DateLookingType;
  dayHover: DateType;
  disabled?: boolean;
  disabledDates?: DateRangeType[] | null;
  displayFormat: string;

  hideDatepicker: () => void;

  i18n: string;
  input?: HTMLInputElement | null;
  inputClassName?: ((className: string) => string) | string | null;
  inputId?: string;
  inputName?: string;
  inputText: string;
  isOpen: boolean;

  maxDate?: DateType | null;
  minDate?: DateType | null;

  period: Period;
  placeholder?: string | null;
  popoverDirection?: PopoverDirectionType;
  primaryColor: ColorKeys;

  readOnly?: boolean;
  required?: boolean;
  separator: string;

  showDatepicker: () => void;
  showFooter?: boolean;
  startWeekOn?: WeekStringType | null;
  setInput: (value: HTMLInputElement | null) => void;

  toggleClassName?: ((className: string) => string) | string | null;
  toggleIcon?: (isEmpty: boolean) => ReactNode;
  trigger?: (props: DatepickerTriggerProps) => ReactNode;

  updateFirstDate: (date: Date) => void;

  value: DateValueType;
};

const DatepickerContext = createContext<DatepickerStore>({
  arrowContainer: null,
  asSingle: false,

  calendarContainer: null,
  cancelDatepicker: () => {},
  changeDatepickerValue: () => {},
  changeDayHover: () => {},
  changeInputText: () => {},
  changePeriod: () => {},
  classNames: undefined,
  configs: undefined,
  containerClassName: "",

  dateLooking: DEFAULT_DATE_LOOKING,
  dayHover: null,
  disabled: false,
  disabledDates: null,
  displayFormat: DATE_FORMAT,

  hideDatepicker: () => {},

  i18n: LANGUAGE,

  input: undefined,
  inputClassName: "",

  inputId: undefined,
  inputName: undefined,
  inputText: "",
  isOpen: false,

  maxDate: null,
  minDate: null,

  period: { start: null, end: null },
  popoverDirection: undefined,
  primaryColor: DEFAULT_COLOR,

  readOnly: false,
  required: false,

  separator: DEFAULT_SEPARATOR,
  showDatepicker: () => {},
  showFooter: false,
  startWeekOn: START_WEEK,
  setInput: () => {},

  toggleClassName: "",
  toggleIcon: undefined,
  trigger: undefined,

  updateFirstDate: () => {},

  value: null,
});

export default DatepickerContext;
